import React, { FC } from 'react';
import _ from 'lodash';
import { useIntl } from 'react-intl';
import { Button } from 'react-bootstrap';
import { ComparisonOperators, Condition, Rule } from '../../../../../models/form/rule';
import { FieldType } from '../../../../../models/form/fieldType';
import { RulesTemplates } from './RulesTemplates';
import { FieldTransformed } from './Rules';
import SelectInput from '../../../../../components/library/inputs/select/SelectInput';
import TextInput from '../../../../../components/library/inputs/text/TextInput';
import NumberInput from '../../../../../components/library/inputs/number/NumberInput';
import { FieldModel } from '../../../../../models/form/field';
import { Option } from '../../../../../models/form/option';
import Datetime from '../../../../../components/library/inputs/dateTime/DateTime';
import StarRating from '../../../../../components/library/inputs/rating/StarRating';
import { getText } from '../../../../../utilities/CultureUtilities';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store';

interface Props {
  renderFieldOptions: (predicate: (field: FieldModel) => boolean, renderAllFields: boolean) => React.ReactNode;
  setRule: (rule: Rule) => void;
  getField: (fieldId: string) => FieldTransformed | undefined;
  getOptions: (fieldId: string) => Option[] | undefined;
  formFields: FieldModel[];
  rule: Rule;
  createNewCondition: () => Condition;
}

const Conditions: FC<Props> = ({
  renderFieldOptions,
  rule,
  setRule,
  getField,
  getOptions,
  formFields,
  createNewCondition,
}: Props) => {
  const intl = useIntl();
  const selectedLanguage = useSelector((state: RootState) => state.formAdmin.selectedLanguage);

  const optionListFields = formFields.filter((field) => {
    return field.type == FieldType.OptionList && !field.multiSelection;
  });

  const removeCondition = (orConditionIndex: number, conditionId: string) => {
    const ruleTemp = { ...rule };
    _.remove(ruleTemp.conditions[orConditionIndex], { id: conditionId });
    if (ruleTemp.conditions[orConditionIndex].length <= 0) {
      ruleTemp.conditions.splice(orConditionIndex, 1);
    }
    setRule(ruleTemp);
  };

  const addAndCondition = (rule: Rule, conditionIndex: number) => {
    const ruleTemp = { ...rule };
    ruleTemp.conditions[conditionIndex].push(createNewCondition());
    setRule(ruleTemp);
  };

  const addOrCondition = (rule: Rule) => {
    const ruleTemp = { ...rule };
    ruleTemp.conditions.push([createNewCondition()]);
    setRule(ruleTemp);
  };

  const renderValueInput = (
    operator: ComparisonOperators,
    orConditionIndex: number,
    andConditionIndex: number,
    value: string | undefined,
    fieldId: string,
  ) => {
    const onChange = (e) => {
      const ruleTemp = { ...rule };
      if (typeof e === 'string') {
        ruleTemp.conditions[orConditionIndex][andConditionIndex].value = e;
      } else if (typeof e === 'number') {
        ruleTemp.conditions[orConditionIndex][andConditionIndex].value = e.toString();
      } else {
        ruleTemp.conditions[orConditionIndex][andConditionIndex].value = e.target.value;
      }
      setRule(ruleTemp);
    };
    const fieldType = getField(fieldId)?.type;
    const options = getOptions(fieldId);
    if (fieldType && operator !== ComparisonOperators.IsCompleted && operator !== ComparisonOperators.IsChecked) {
      const input = RulesTemplates[fieldType].inputField;
      switch (input.type) {
        case FieldType.Text:
          return (
            <div className="d-flex justify-content-center align-items-center" style={{ flex: 1, paddingRight: 8 }}>
              <TextInput style={{ height: 38 }} size="sm" value={value} onChange={onChange} />
            </div>
          );
        case FieldType.Datetime:
          return (
            <div className="d-flex justify-content-center align-items-center" style={{ flex: 1, paddingRight: 8 }}>
              <Datetime
                id={fieldId}
                enabled={true}
                placeholder={undefined}
                value={value ? value : undefined}
                mode={input.instance}
                onClose={undefined}
                onChange={onChange}
                style={{ height: 38 }}
              />
            </div>
          );
        case FieldType.Numeric:
          return (
            <div className="d-flex justify-content-center align-items-center" style={{ flex: 1, paddingRight: 8 }}>
              <NumberInput
                size="sm"
                value={value ? parseFloat(value) : undefined}
                onChange={onChange}
                style={{ height: 38 }}
              />
            </div>
          );
        case FieldType.Select:
          if (options) {
            return (
              <div className="d-flex justify-content-center align-items-center" style={{ flex: 1, paddingRight: 8 }}>
                <SelectInput
                  size="sm"
                  value={value ? value : undefined}
                  onChange={(e) => onChange(e)}
                  options={options.map((option) => {
                    return {
                      value: option.value,
                      text:
                        fieldType == FieldType.YesNo
                          ? intl.formatMessage({ id: getText(option.text, selectedLanguage) })
                          : option.text,
                    };
                  })}
                  style={{ height: 38 }}
                />
              </div>
            );
          }
          break;
        case FieldType.Rating:
          return (
            <div className="d-flex justify-content-center align-items-center" style={{ flex: 1, paddingRight: 8 }}>
              <StarRating
                style={{ height: 38 }}
                color={'#ffffff'}
                value={value && parseFloat(value) ? parseFloat(value) : 0}
                count={5}
                fontSize={30}
                allowHalf={true}
                onChange={onChange}
                readOnlyMode={false}
              />
            </div>
          );
        default:
          break;
      }
    }
  };

  const renderPossibleComparisonOperators = (
    fieldType: FieldType | undefined,
    operator: ComparisonOperators,
    orConditionIndex: number,
    andConditionIndex: number,
  ) => {
    if (!fieldType) {
      return null;
    }
    const multiselectionField = optionListFields.filter(
      (field) => field.id == rule.conditions[orConditionIndex][andConditionIndex].fieldId,
    );
    function getFilterOperators() {
      const operators: ComparisonOperators[] = [];
      if (fieldType) {
        RulesTemplates[fieldType].operators.map((operator) => {
          if (
            operator != ComparisonOperators.Includes ||
            (operator == ComparisonOperators.Includes && multiselectionField.length == 0)
          ) {
            operators.push(operator);
          }
        });
      }
      return operators;
    }
    const validOperators = getFilterOperators();
    return (
      <SelectInput
        noEmptyValue
        size="sm"
        value={operator}
        onChange={(e) => {
          const ruleTemp = { ...rule };
          ruleTemp.conditions[orConditionIndex][andConditionIndex].comparisonOperator = e.target
            .value as ComparisonOperators;
          ruleTemp.conditions[orConditionIndex][andConditionIndex].value = undefined;
          setRule(ruleTemp);
        }}
        options={validOperators.map((template) => {
          return {
            value: template,
            text: intl.formatMessage({ id: template }),
          };
        })}
        style={{ height: 38 }}
      />
    );
  };

  const renderFieldSelectCondition = (
    rule: Rule,
    fieldId: string,
    orConditionIndex: number,
    andConditionIndex: number,
  ) => {
    return (
      <SelectInput
        noEmptyValue
        size="sm"
        value={fieldId}
        onChange={(e) => {
          const ruleTemp = { ...rule };
          const newFieldId = e.target.value;
          const newField = getField(newFieldId);
          ruleTemp.conditions[orConditionIndex][andConditionIndex].fieldId = newFieldId;
          ruleTemp.conditions[orConditionIndex][andConditionIndex].value = undefined;
          if (newField) {
            ruleTemp.conditions[orConditionIndex][andConditionIndex].comparisonOperator =
              RulesTemplates[newField.type].operators[0];
          }
          setRule(ruleTemp);
        }}
        customOptions={renderFieldOptions(
          (field: FieldModel) =>
            RulesTemplates[field.type].operators && RulesTemplates[field.type].operators.length > 0,
          true,
        )}
        style={{ height: 38 }}
      />
    );
  };

  return (
    <div>
      {rule.conditions.map((andConditions, orConditionIndex) => {
        return (
          <div key={'andCondition' + orConditionIndex}>
            {andConditions.map((condition, andConditionIndex) => {
              return (
                <div key={'condition' + andConditionIndex + condition.comparisonOperator}>
                  <div className="d-flex mb-2">
                    {(andConditions.length !== 1 || rule.conditions.length !== 1) && (
                      <div style={{ marginRight: 8 }}>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => removeCondition(orConditionIndex, condition.id)}
                        >
                          &times;
                        </Button>
                      </div>
                    )}
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ flex: 1, paddingRight: 8 }}
                    >
                      {renderFieldSelectCondition(rule, condition.fieldId, orConditionIndex, andConditionIndex)}
                    </div>
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ flex: 1, paddingRight: 8 }}
                    >
                      {renderPossibleComparisonOperators(
                        getField(condition.fieldId)?.type,
                        condition.comparisonOperator,
                        orConditionIndex,
                        andConditionIndex,
                      )}
                    </div>
                    {renderValueInput(
                      condition.comparisonOperator,
                      orConditionIndex,
                      andConditionIndex,
                      condition.value,
                      condition.fieldId,
                    )}
                    <div className="d-flex justify-content-end align-items-center" style={{ flex: 0 }}>
                      {andConditions[andConditionIndex + 1] && (
                        <Button size="sm" variant="light" style={{ background: 'white', width: 60, height: 38 }}>
                          {intl.formatMessage({ id: 'rulesAdmin.general.and' })}
                        </Button>
                      )}
                      {!andConditions[andConditionIndex + 1] && (
                        <div className="d-flex justify-content-end align-items-center" style={{ flex: 0 }}>
                          <Button
                            variant="light"
                            size="sm"
                            style={{ width: 60, height: 38 }}
                            onClick={() => addAndCondition(rule, orConditionIndex)}
                          >
                            + {intl.formatMessage({ id: 'rulesAdmin.general.and' })}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {rule.conditions[orConditionIndex + 1] && (
              <div
                style={{ textAlign: 'center', borderBottom: '1px solid whitesmoke', marginTop: 6, marginBottom: 30 }}
              >
                <Button style={{ marginBottom: -20, background: 'white' }} size="sm" variant="light">
                  {intl.formatMessage({ id: 'rulesAdmin.general.or' })}
                </Button>
              </div>
            )}
            {!rule.conditions[orConditionIndex + 1] && (
              <div
                style={{ textAlign: 'center', borderBottom: '1px solid whitesmoke', marginTop: 6, marginBottom: 20 }}
              >
                <Button
                  style={{ marginBottom: -20, height: 38 }}
                  size="sm"
                  variant="light"
                  onClick={() => addOrCondition(rule)}
                >
                  + {intl.formatMessage({ id: 'rulesAdmin.general.or' })}
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Conditions;
